import Controller, { OnBlur, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ApplicationRoute } from '../../../../constants/routes';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import SolutionService, { SolutionPartialVolumeParameters } from '../../services/solution-service';
import { ButtonAPI, FormAPI, InputAPI, TextAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import { ManagementSolution } from '../../services/solution-service';
import moment from 'moment';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import './infusion-register-partial-volume-controller.css';
import RouteUtils, { RouteError } from '../../../../utils/route-utils';
import { InfusionInterruptionBaseController } from '../infusion-interrupt-base-controller';
import { SimpleDialog } from '../../../dialog-helper';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { InfusionsViewerParams } from '../../infusion-viewer/infusions-viewer-params';

interface RecordPartialVolumeFormData {
  justificationDescription: string;
  partialLiquidInput: number;
  volumeAccumulatedInput: number;
}

@Controller({ component: 'infusion-record-partial-volume-layout', fragment: 'infusion-record-partial-volume' })
class RegisterPartialVolumeController extends InfusionInterruptionBaseController {

  @Inject('confirm-interrupt-solution-button')
  confirmButton!: ButtonAPI;

  @Inject('partial-liquid-input')
  partialLiquidInput!: InputAPI;

  @Inject('volume-accumulated-input')
  volumeAccumulatedInput!: InputAPI;

  @Inject('volume-step-input')
  volumeStepInputApi!: InputAPI;

  @Inject('volume-partial-input')
  volumePartialInputApi!: InputAPI;

  @Inject('record-partial-volume-form')
  recordPartialVolumeForm!: FormAPI;

  @Inject('volume-remarks-text-area')
  volumeRemarksTextArea!: TextAreaAPI;

  @Inject('partial-liquid-unit')
  partialLiquidUnitAPI!: TextAPI;

  @Inject('volume-accumulated-unit')
  volumeAccumulatedUnitAPI!: TextAPI;

  solutionService = new SolutionService();

  private async getManagementSolution(): Promise<void> {
    const prescriptionNumber = this.context.get('prescriptionNumber') as number;
    const solutionSequence = InfusionsViewerParams.adminSchedule.solutionSequenceNumber as number;

    const routeSafeGetManagementSolution = RouteUtils.routeSafeAsync(this.solutionService.getManagementSolution);

    try {
      const managementSolution = await routeSafeGetManagementSolution(
        prescriptionNumber,
        solutionSequence
      )

      const isoCurrentDate = moment().format(ISO_FULL_DATE_FORMAT);

      this.context.set({
        inInfusionInterruptRouteStack: false,
        isoCurrentDate,
        managementSolution,
      });

      const solutionDetail = {
        date: isoCurrentDate,
        emarVolumeQuantity: managementSolution.emarVolumeQuantity,
        partialVolumeQuantityBI: managementSolution.partialVolumeQuantityBI,
        solutionDescription: managementSolution.solutionDescription,
        unitMeasureCode: managementSolution.unitMeasureCode,
      }

      this.partialLiquidUnitAPI.setValue(String(managementSolution.unitMeasureCode));
      this.volumeAccumulatedUnitAPI.setValue(String(managementSolution.unitMeasureCode));
      this.volumeStepInputApi.setValue(`${solutionDetail.emarVolumeQuantity || '0'} ${solutionDetail.unitMeasureCode}`);
      this.volumePartialInputApi.setValue(`${solutionDetail.partialVolumeQuantityBI || '0'} ${solutionDetail.unitMeasureCode}`);

      this.context.set({ solution: solutionDetail });
      const event = new CustomEvent('fillSolutionDetail', { detail: { solutionDetail } });
      dispatchEvent(event);

    } catch(error) {
      const isRouteError = error instanceof RouteError;
      if(!isRouteError) {
        SimpleDialog.showErrorMessage(error as string);
      }
    }
  }

  private recordPartialVolumeSolution(): void {
    const form: FormAPI = this.recordPartialVolumeForm;
    const record = form.getRecord();
    const recordPartialVolumeFormData = record as Record<string | number, RecordPartialVolumeFormData>;

    const justificationDescription = recordPartialVolumeFormData.justificationDescription as unknown as string;
    const partialLiquidInput = recordPartialVolumeFormData.partialLiquidInput as unknown as number;

    const encounterNumber = this.context.get('encounterNumber') as number;
    const prescriptionNumber = this.context.get('prescriptionNumber') as number;
    const solutionSequence = this.context.get('solutionSequence') as number;
    const isoCurrentDate = this.context.get('isoCurrentDate') as string;
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const params: SolutionPartialVolumeParameters = {
      encounterNumber: encounterNumber,
      justificationDescription: justificationDescription,
      prescriptionNumber: prescriptionNumber,
      solutionSequence: solutionSequence,
      volumeDate: isoCurrentDate,
      volumePhase: Number(managementSolution.emarVolumeQuantity),
      volumeQuantity: partialLiquidInput,
    }

    this.solutionService.registerPartialVolumeSolution(params);
  }

  private redirectInterrupetSolution(): void {
    const route = ApplicationRoute.EMAR_INTERRUPT_SOLUTION;

    const patientCode = this.context.get('patientCode') as number;
    const encounterNumber = this.context.get('encounterNumber') as number;
    const prescriptionNumber = this.context.get('prescriptionNumber') as number;
    const solutionSequence = this.context.get('solutionSequence') as number;

    const { startDate, hourStatus, itemStatus, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();

    this.context.set({inInfusionInterruptRouteStack: true});

    applicationContext.redirect(
      route,
      {
        currentSpeedInfusion,
        encounterId: encounterNumber,
        hourStatus,
        itemStatus,
        patientId: patientCode,
        prescriptionNumber: prescriptionNumber,
        sequenceNumber: solutionSequence,
        startDate,
      }
    );
  }

  private calculateVolumeAccumulated(partialLiquid: number): number {
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const partialVolumeQuantity = Number(managementSolution?.partialVolumeQuantity);
    const volumeAccumulated = partialLiquid + partialVolumeQuantity;
    return volumeAccumulated;
  }

  private calculatePartialLiquid(volumeAccumulated: number): number {
    const managementSolution = this.context.get('managementSolution') as ManagementSolution;

    const partialVolumeQuantity = Number(managementSolution?.partialVolumeQuantity);
    const partialLiquid = volumeAccumulated - partialVolumeQuantity;
    return partialLiquid;
  }

  @OnMount({ target: 'infusion-record-partial-volume-layout' })
  infusionRecordPartialVolumeLayoutMount(): void {
    InformationBarUtils.collapsedBar = true;

    InfusionInterruptionBaseController.routeInStack.infusionPartialVolume = true;

    const {
      patientId,
      encounterId,
      prescriptionNumber,
      sequenceNumber
    } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      encounterNumber: encounterId as number,
      patientCode: patientId as number,
      patientId,
      prescriptionNumber: prescriptionNumber as number,
      solutionSequence: sequenceNumber as number,
    })

    this.getManagementSolution();
  }

  @OnUnmount({ target: 'infusion-record-partial-volume-layout' })
  infusionStopSolutionLayoutUnmount(): void {
    const inInfusionInterruptRouteStack = this.context.get('inInfusionInterruptRouteStack') as boolean;
    InfusionInterruptionBaseController.routeInStack.infusionPartialVolume = inInfusionInterruptRouteStack;
  }

  @OnClick({ target: 'cancel-interrupt-solution-button' })
  cancelInterruptSolutionButtonClick(): void {
    this.redirectInterrupetSolution();
  }

  @OnClick({ target: 'confirm-interrupt-solution-button' })
  async confirmInterruptSolutionButtonClick(): Promise<void> {
    const valid = await this.recordPartialVolumeForm.validate();

    if(valid) {
      this.recordPartialVolumeSolution();
      this.redirectInterrupetSolution();
    }
  }

  @OnBlur({ target: 'partial-liquid-input' })
  onPartialLiquidInputBlur(): void {
    const partialLiquid = this.partialLiquidInput.getValue();

    if(String(partialLiquid) !== '0' && !partialLiquid){
      return;
    }

    const newVolumeAccumulated = this.calculateVolumeAccumulated(Number(partialLiquid));
    this.volumeAccumulatedInput.setValue(newVolumeAccumulated);
  }

  @OnBlur({ target: 'volume-accumulated-input' })
  onVolumeAccumulatedInputBlur(): void {
    const volumeAccumulated = this.volumeAccumulatedInput.getValue();

    if(String(volumeAccumulated) !== '0' && !volumeAccumulated){
      return;
    }

    const newPartialLiquid = this.calculatePartialLiquid(Number(volumeAccumulated));
    this.partialLiquidInput.setValue(newPartialLiquid);
  }
}

export default RegisterPartialVolumeController;